// src/components/contacts/EditContactDrawer.tsx
import React, { useEffect, useState } from 'react';
import { Drawer, Form, Input, Button, Space, Select, Switch, message } from 'antd';
import { type ContactDTO, updateContact } from '../../services/contacts';
import { listSectors, type SectorDTO } from '../../services/sector';

type Props = {
  open: boolean;
  contact: ContactDTO | null;
  onClose: () => void;
  onSaved?: () => void;
};

const EditContactDrawer: React.FC<Props> = ({ open, contact, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [sectors, setSectors] = useState<SectorDTO[]>([]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const sec = await listSectors(true);
        setSectors(sec || []);
      } catch {}
    })();
  }, [open]);

  useEffect(() => {
    if (contact) {
      form.setFieldsValue({
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        businessName: contact.businessName || '',
        email: contact.email || '',
        phone: contact.phone || '',
        website: contact.website || '',
        notes: contact.notes || '',
        sectorId: contact.sector?.id,
        active: contact.isActive !== false,
      });
    } else {
      form.resetFields();
    }
  }, [contact]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!contact) return;
    try {
      const values = await form.validateFields();
      setLoading(true);
      await updateContact(contact.id, {
        firstName: values.firstName || undefined,
        lastName: values.lastName || undefined,
        businessName: values.businessName || undefined,
        email: values.email || undefined,
        phone: values.phone || undefined,
        website: values.website || undefined,
        notes: values.notes || undefined,
        sectorId: values.sectorId ?? null,
        active: values.active,
      });
      message.success('Contatto aggiornato');
      onSaved?.();
      onClose();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Errore salvataggio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title="Modifica Contatto"
      open={open}
      onClose={onClose}
      width={520}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose}>Annulla</Button>
          <Button type="primary" onClick={handleSave} loading={loading}>Salva</Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <Space.Compact block style={{ gap: 12 }}>
          <Form.Item name="firstName" label="Nome" style={{ flex: 1 }}>
            <Input placeholder="Mario" />
          </Form.Item>
          <Form.Item name="lastName" label="Cognome" style={{ flex: 1 }}>
            <Input placeholder="Rossi" />
          </Form.Item>
        </Space.Compact>

        <Form.Item name="businessName" label="Azienda">
          <Input placeholder="Azienda S.r.l." />
        </Form.Item>

        <Space.Compact block style={{ gap: 12 }}>
          <Form.Item name="email" label="Email" style={{ flex: 1 }} rules={[{ type: 'email', message: 'Email non valida' }]}>
            <Input placeholder="mario.rossi@azienda.it" />
          </Form.Item>
          <Form.Item name="phone" label="Telefono" style={{ flex: 1 }}>
            <Input placeholder="+39 333 1234567" />
          </Form.Item>
        </Space.Compact>

        <Form.Item name="website" label="Sito web">
          <Input placeholder="https://www.azienda.it" />
        </Form.Item>

        <Form.Item name="sectorId" label="Settore">
          <Select
            allowClear
            showSearch
            placeholder="Seleziona settore"
            optionFilterProp="label"
            options={(sectors || []).map(s => ({ label: s.name, value: s.id }))}
          />
        </Form.Item>

        <Form.Item name="notes" label="Note">
          <Input.TextArea placeholder="Note interne..." autoSize={{ minRows: 3 }} />
        </Form.Item>

        <Form.Item name="active" label="Attivo" valuePropName="checked" initialValue={true}>
          <Switch />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default EditContactDrawer;
